import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Select from 'react-select';
import Sliders from 'feather-icons-react/build/IconComponents/Sliders';
import { ChevronUp, Filter, PlusCircle, RotateCcw, StopCircle, Calendar, Eye } from 'feather-icons-react/build/IconComponents';
import { DatePicker } from 'antd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { setToogleHeader } from '../../core/redux/action';
import { productActions } from "../../core/action/productActions";
import { categoryActions } from "../../core/action/categoryaction";
import { brandActions } from "../../core/action/brandaction";
import { userActions } from "../../core/action/userAction";
import AddProductModal from '../inventory/addproduct';
import ViewProductModal from '../inventory/viewProduct';
import moment from 'moment';
import withReactContent from "sweetalert2-react-content";

const ProductList = () => {
    const dataSource = useSelector((state) => state.product_list || []);
    //const ProductData = useSelector((state) => state.selected_product || []);
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    const categories = useSelector((state) => state.category_list || []);
    const brands = useSelector((state) => state.brand_list || []);
    const MySwal = withReactContent(Swal);

    // Permission checks
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.role_slug === 'super-admin';
    const isAdminUser = user.role_slug === 'admin' || user.role_slug === 'super-admin';
    const canCreate = isSuperAdmin || userActions.hasPermission('create-products');
    const canEdit = isAdminUser;
    const canDelete = isSuperAdmin || userActions.hasPermission('delete-products');
    const canView = isSuperAdmin || userActions.hasPermission('view-products');
    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [loadingEdit, setLoadingEdit] = useState(false);

    // Filter states
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        category: '',
        brand: '',
        date: ''
    });
    const [selectedDate, setSelectedDate] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    const [sortConfig, setSortConfig] = useState({ value: 'newest', label: 'Newest' });

    // Filter data client-side based on search term (fallback while API loads)
    const getFilteredData = useCallback(() => {
        return dataSource || [];
    }, [dataSource]);

    const oldandlatestvalue = [
        { value: "date", label: "Sort by Date" },
        { value: "newest", label: "Newest" },
        { value: "oldest", label: "Oldest" },
    ];

    const statusOptions = [
        { value: "", label: "Choose Status" },
        { value: "Active", label: "Active" },
        { value: "InActive", label: "InActive" },
    ];

    // Prepare category options
    const categoryOptions = [
        { value: "", label: "Choose Category" },
        ...(categories.map(cat => ({
            value: cat.id,
            label: cat.Category || cat.name || cat.category_name
        })))
    ];

    // Prepare brand options
    const brandOptions = [
        { value: "", label: "Choose Brand" },
        ...(brands.map(brand => ({
            value: brand.id,
            label: brand.Brand || brand.name || brand.brand_name
        })))
    ];

    // Fetch products with useCallback
    const fetchProducts = useCallback(async (overrideSearch = null, overrideFilters = null, overridePage = null) => {
        const activeSearch = overrideSearch !== null ? overrideSearch : searchTerm;
        const activeFilters = overrideFilters !== null ? overrideFilters : filters;
        const activePage = overridePage !== null ? overridePage : pagination.page;
        setLoading(true);
        try {
            const params = {
                page: activePage,
                limit: pagination.limit,
                search: activeSearch,
                status: activeFilters.status,
                category: activeFilters.category,
                brand: activeFilters.brand,
                start_date: activeFilters.date,
                end_date: activeFilters.date,
                sort_by: sortConfig.value === 'date' ? 'created_at' : 'created_at',
                sort_order: sortConfig.value === 'oldest' ? 'ASC' : 'DESC'
            };

            const result = await dispatch(productActions.fetchProducts(params));
            if (result.success && result.data.pagination) {
                setPagination(prev => ({
                    ...prev,
                    total: result.data.pagination.total_items || 0,
                    totalPages: result.data.pagination.total_pages || 1
                }));
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, pagination.page, pagination.limit, searchTerm, filters, sortConfig]);

    // Fetch categories and brands
    const fetchDropdownData = useCallback(async () => {
        try {
            await dispatch(categoryActions.fetchCategories({}));
            await dispatch(brandActions.fetchBrands({}));
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    }, [dispatch]);

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        const newFilters = { ...filters, [filterName]: value };
        setFilters(newFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchProducts(null, newFilters);
    };

    // Handle date change
    const handleDateChange = (date) => {
        setSelectedDate(date);
        if (date) {
            handleFilterChange('date', moment(date).format('YYYY-MM-DD'));
        } else {
            handleFilterChange('date', '');
        }
    };

    // Apply filters
    const handleApplyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchProducts(searchTerm, filters);
        setIsFilterVisible(false);
    };

    // Clear filters
    const handleClearFilters = () => {
        const emptyFilters = { status: '', category: '', brand: '', date: '' };
        setFilters(emptyFilters);
        setSelectedDate(null);
        setSearchTerm('');
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchProducts('', emptyFilters);
    };

    // Handle sort change
    const handleSortChange = (option) => {
        setSortConfig(option);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Handle add product
    const handleAddProduct = () => {
        if (!canCreate) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to add products',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK'
            });
            return;
        }
        setShowAddModal(true);
    };

    // Handle view product
    const handleViewProduct = (record) => {
        setSelectedProduct(record);
        setShowViewModal(true);
    };

    // Handle edit product - Fetch complete data first
    const handleEditProduct = async (record) => {
        if (!canEdit) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'Only admin can edit Cost Price and Selling Price',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK'
            });
            return;
        }
        setLoadingEdit(true);
        try {
            const result = await dispatch(productActions.fetchProductById(record.id));
            if (result.success) {
                setSelectedProduct(result.data);
                setSelectedProductId(record.id);
                setShowEditModal(true);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load product details',
                    confirmButtonColor: '#ff0000'
                });
            }
        } catch (error) {
            console.error('Error loading product:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load product details',
                confirmButtonColor: '#ff0000'
            });
        } finally {
            setLoadingEdit(false);
        }
    };

    // Handle delete product
    const handleDeleteProduct = async (id) => {
        if (!canDelete) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to delete products',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK'
            });
            return;
        }
        const confirm = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#6c757d',
            cancelButtonText: 'Cancel',
        });
        if (!confirm.isConfirmed) return;

        setLoading(true);
        try {
            const deleteResult = await dispatch(productActions.deleteProduct(id));
            if (deleteResult.success) {
                // Immediately remove from Redux store — no waiting for API refetch
                dispatch({ type: 'Product_delete_success', payload: id });
                await MySwal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Product deleted successfully',
                    timer: 1500,
                    showConfirmButton: false
                });
                // Also refresh from server to get updated pagination
                fetchProducts('', filters, 1);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: deleteResult.message || 'Failed to delete product'
                });
            }
        } catch (error) {
            MySwal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete product' });
        } finally {
            setLoading(false);
        }
    };

    // Handle refresh
    const handleRefresh = () => {
        fetchProducts();
    };

    // Handle page change
    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
    };

    // Handle limit change
    const handleLimitChange = (e) => {
        setPagination(prev => ({
            ...prev,
            limit: parseInt(e.target.value),
            page: 1
        }));
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            width: 70
        },
        {
            title: "Product",
            dataIndex: "Product",
            render: (text, record) => (
                <div className="d-flex align-items-center">
                    <div>
                        <div className="fw-semibold">{text || record.product_name}</div>
                        <small className="text-muted">{record.sku}</small>
                    </div>
                </div>
            )
        },
        {
            title: "HSN Code",
            dataIndex: "hsn_code",
            render: (text) => text || '-'
        },
        {
            title: "SKU",
            dataIndex: "sku",
        },
        {
            title: "Category",
            dataIndex: "category",
            render: (text) => text || '-'
        },
        {
            title: "Brand",
            dataIndex: "brand",
            render: (text) => text || '-'
        },
        {
            title: "Unit",
            dataIndex: "unit",
            render: (text) => text || '-'
        },
        {
            title: "Cost Price",
            dataIndex: "cost_price",
            render: (text) => text ? `₹${parseFloat(text).toFixed(2)}` : '₹0.00'
        },
        {
            title: "Selling Price",
            dataIndex: "selling_price",
            render: (text) => text ? `₹${parseFloat(text).toFixed(2)}` : '₹0.00'
        },
        {
            title: "Qty",
            dataIndex: "quantity",
            render: (text) => text || '0'
        },
        {
            title: "Status",
            dataIndex: "Status",
            render: (text) => (
                <span className={`badge ${text === 'Active' || text === 'active' ? 'badge-success' : 'badge-danger'}`}>
                    {text}
                </span>
            )
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            width: 150,
            render: (_text, record) => (
                <div className="d-flex">
                    {canView && (
                        <button
                            className="btn btn-sm btn-outline-info me-1"
                            onClick={() => handleViewProduct(record)}
                            title="View"
                            disabled={loadingEdit}
                        >
                            <Eye size={14} />
                        </button>
                    )}
                    {canEdit && (
                        <button
                            className="btn btn-sm btn-outline-warning me-1"
                            onClick={() => handleEditProduct(record)}
                            title="Edit"
                            disabled={loadingEdit}
                        >
                            <i className="feather-edit" style={{ fontSize: '14px' }}></i>
                        </button>
                    )}
                    {canDelete && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteProduct(record.id)}
                            title="Delete"
                        >
                            <i className="feather-trash-2" style={{ fontSize: '14px' }}></i>
                        </button>
                    )}
                </div>
            )
        }
    ];

    // Initial load
    useEffect(() => {
        fetchDropdownData();
        fetchProducts();
    }, []);

    // Re-fetch when sort, page, or limit changes
    useEffect(() => {
        fetchProducts();
    }, [sortConfig, pagination.page, pagination.limit]);

    // Debounced server-side search: whenever searchTerm changes, go back to page 1 and fetch
    useEffect(() => {
        const timer = setTimeout(() => {
            setPagination(prev => ({ ...prev, page: 1 }));
            fetchProducts(searchTerm, filters);
        }, 350);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    return (
        <>
            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header">
                        <div className="add-item d-flex">
                            <div className="page-title">
                                <h4>Product List</h4>
                                <h6>Manage your products</h6>
                            </div>
                        </div>
                        <ul className="table-top-head">


                            <li>
                                <OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
                                    <Link onClick={handleRefresh}>
                                        <RotateCcw />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                            <li>
                                <OverlayTrigger placement="top" overlay={<Tooltip>Collapse</Tooltip>}>
                                    <Link
                                        id="collapse-header"
                                        className={data ? "active" : ""}
                                        onClick={() => { dispatch(setToogleHeader(!data)) }}
                                    >
                                        <ChevronUp />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                        </ul>
                        <div className="page-btn">
                            {canCreate && (
                                <button
                                    className="btn btn-added"
                                    onClick={handleAddProduct}
                                    disabled={loading}
                                >
                                    <PlusCircle className="me-2" />
                                    Add New Product
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="card table-list-card">
                        <div className="card-body">
                            <div className="table-top">
                                <div className="search-set">
                                    <div className="search-input">
                                        <input
                                            type="text"
                                            placeholder="Search by product name, SKU, or HSN code"
                                            className="form-control form-control-sm formsearch"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="search-path">
                                    <button
                                        className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                                        onClick={() => setIsFilterVisible(!isFilterVisible)}
                                    >
                                        <Filter className="filter-icon" />
                                        Filter
                                    </button>
                                </div>
                                <div className="form-sort">
                                    <Sliders className="info-img" />
                                    <Select
                                        className="select"
                                        options={oldandlatestvalue}
                                        value={sortConfig}
                                        onChange={handleSortChange}
                                        placeholder="Newest"
                                        isDisabled={loading}
                                    />
                                </div>
                            </div>

                            {/* Filter Section */}
                            {isFilterVisible && (
                                <div className="card mt-3">
                                    <div className="card-body pb-0">
                                        <div className="row">
                                            <div className="col-lg-3 col-sm-6 col-12">
                                                <div className="input-blocks">
                                                    <StopCircle className="info-img" />
                                                    <Select
                                                        className="select"
                                                        options={statusOptions}
                                                        placeholder="Choose Status"
                                                        value={statusOptions.find(opt => opt.value === filters.status)}
                                                        onChange={(selected) => handleFilterChange('status', selected?.value || '')}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-3 col-sm-6 col-12">
                                                <div className="input-blocks">
                                                    <StopCircle className="info-img" />
                                                    <Select
                                                        className="select"
                                                        options={categoryOptions}
                                                        placeholder="Choose Category"
                                                        value={categoryOptions.find(opt => opt.value === filters.category)}
                                                        onChange={(selected) => handleFilterChange('category', selected?.value || '')}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-3 col-sm-6 col-12">
                                                <div className="input-blocks">
                                                    <StopCircle className="info-img" />
                                                    <Select
                                                        className="select"
                                                        options={brandOptions}
                                                        placeholder="Choose Brand"
                                                        value={brandOptions.find(opt => opt.value === filters.brand)}
                                                        onChange={(selected) => handleFilterChange('brand', selected?.value || '')}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-3 col-sm-6 col-12">
                                                <div className="input-blocks">
                                                    <Calendar className="info-img" />
                                                    <DatePicker
                                                        selected={selectedDate}
                                                        onChange={handleDateChange}
                                                        className="form-control"
                                                        dateFormat="dd-MM-yyyy"
                                                        placeholderText="Choose Date"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-12 mt-3">
                                                <div className="d-flex gap-2 justify-content-end">
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={handleApplyFilters}
                                                        disabled={loading}
                                                    >
                                                        <i className="feather-search me-1" /> Apply Filters
                                                    </button>
                                                    <button
                                                        className="btn btn-secondary"
                                                        onClick={handleClearFilters}
                                                        disabled={loading}
                                                    >
                                                        Clear Filters
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Table */}
                            <div className="table-responsive mt-3">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading products...</p>
                                    </div>
                                ) : getFilteredData().length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="feather-package" style={{ fontSize: '48px', opacity: 0.5 }} />
                                        <h5 className="mt-3">No products found</h5>
                                        <p className="text-muted">
                                            {searchTerm || Object.values(filters).some(v => v)
                                                ? "No products match your search. Try a different keyword."
                                                : "Add your first product to get started"}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <table className="table table-hover">
                                            <thead>
                                                <tr>
                                                    {columns.map((col, index) => (
                                                        <th key={index} style={{ whiteSpace: 'nowrap' }}>{col.title}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getFilteredData().map((record) => (
                                                    <tr key={record.id}>
                                                        {columns.map((col, colIndex) => (
                                                            <td key={colIndex}>
                                                                {col.render
                                                                    ? col.render(record[col.dataIndex], record)
                                                                    : record[col.dataIndex]}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Pagination */}
                                        {(() => {
                                            //const filtered = getFilteredData();
                                            const showPageNav = pagination.totalPages > 1;

                                            return (pagination.total > 0) ? (
                                                <div className="pagination-wrapper mt-3">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div className="pagination-info text-muted">
                                                            {searchTerm
                                                                ? `Found ${pagination.total} result${pagination.total !== 1 ? 's' : ''} for "${searchTerm}"`
                                                                : `Showing ${((pagination.page - 1) * pagination.limit) + 1} to ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} entries`
                                                            }
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <select
                                                                className="form-select form-select-sm"
                                                                value={pagination.limit}
                                                                onChange={handleLimitChange}
                                                                style={{ width: 'auto' }}
                                                                disabled={loading}
                                                            >
                                                                <option value="10">10 per page</option>
                                                                <option value="20">20 per page</option>
                                                                <option value="50">50 per page</option>
                                                                <option value="100">100 per page</option>
                                                            </select>

                                                            {showPageNav && (
                                                                <nav>
                                                                    <ul className="pagination pagination-sm mb-0">
                                                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                                            <button
                                                                                className="page-link"
                                                                                onClick={() => handlePageChange(pagination.page - 1)}
                                                                                disabled={pagination.page === 1 || loading}
                                                                            >
                                                                                Previous
                                                                            </button>
                                                                        </li>

                                                                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                                                            let pageNum;
                                                                            if (pagination.totalPages <= 5) {
                                                                                pageNum = i + 1;
                                                                            } else if (pagination.page <= 3) {
                                                                                pageNum = i + 1;
                                                                            } else if (pagination.page >= pagination.totalPages - 2) {
                                                                                pageNum = pagination.totalPages - 4 + i;
                                                                            } else {
                                                                                pageNum = pagination.page - 2 + i;
                                                                            }

                                                                            return (
                                                                                <li
                                                                                    key={pageNum}
                                                                                    className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}
                                                                                >
                                                                                    <button
                                                                                        className="page-link"
                                                                                        onClick={() => handlePageChange(pageNum)}
                                                                                        disabled={loading}
                                                                                    >
                                                                                        {pageNum}
                                                                                    </button>
                                                                                </li>
                                                                            );
                                                                        })}

                                                                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                                            <button
                                                                                className="page-link"
                                                                                onClick={() => handlePageChange(pagination.page + 1)}
                                                                                disabled={pagination.page === pagination.totalPages || loading}
                                                                            >
                                                                                Next
                                                                            </button>
                                                                        </li>
                                                                    </ul>
                                                                </nav>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null;
                                        })()}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <AddProductModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={() => {
                    setShowAddModal(false);
                    setPagination(prev => ({ ...prev, page: 1 }));
                    fetchProducts('', filters);
                }}
            />

            <AddProductModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedProduct(null);
                }}
                isEditMode={true}
                productId={selectedProductId}
                productData={selectedProduct}
                onSuccess={() => {
                    setShowEditModal(false);
                    setSelectedProduct(null);
                    setPagination(prev => ({ ...prev, page: 1 }));
                    fetchProducts('', filters);
                }}
            />

            <ViewProductModal
                isOpen={showViewModal}
                onClose={() => {
                    setShowViewModal(false);
                    setSelectedProduct(null);
                }}
                productData={selectedProduct}
            />
        </>
    );
};

export default ProductList;
